<template>
    <v-card outlined>
      <v-card-text>
        <div class="d-flex align-center mb-4">
          <div class="text-h6">Pagos Programados</div>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            small
            @click="abrirModalPagoProgramado"
          >
            <v-icon left small>mdi-calendar-clock</v-icon>
            Agregar pago programado
          </v-btn>
        </div>

        <!-- Tabla de pagos programados -->
        <v-data-table
          :headers="pagosProgramadosHeaders"
          :items="pagosProgramados"
          :items-per-page="5"
          class="elevation-1"
          :loading="loadingPagosProgramados"
          loading-text="Cargando... Por favor, espere"
          no-data-text="No hay pagos programados disponibles"
        >
          <template v-slot:item.fechaVencimiento="{ item }">
            {{ formatFecha(item.fechaVencimiento) }}
          </template>

          <template v-slot:item.monto_solicitado="{ item }">
            {{ formatCurrency(item.monto_solicitado) }}
          </template>

          <template v-slot:item.comentarios="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on" class="text-truncate d-inline-block" style="max-width: 150px;">
                  {{ item.comentarios || 'Sin comentarios' }}
                </span>
              </template>
              <span>{{ item.comentarios || 'Sin comentarios' }}</span>
            </v-tooltip>
          </template>

          <template v-slot:item.evidencia="{ item }">
            <v-tooltip bottom v-if="item.evidencia">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  x-small
                  icon
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  @click="verEvidencia(item.evidencia)"
                >
                  <v-icon small>mdi-image</v-icon>
                </v-btn>
              </template>
              <span>Ver evidencia de pago</span>
            </v-tooltip>
            <span v-else>-</span>
          </template>

          <template v-slot:item.actions="{ item }">
            <div class="d-flex justify-center align-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    x-small
                    icon
                    color="info"
                    class="mx-1"
                    v-bind="attrs"
                    v-on="on"
                    @click="editarPagoProgramado(item)"
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>Editar pago programado</span>
              </v-tooltip>
              
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    x-small
                    icon
                    color="primary"
                    class="mx-1"
                    v-bind="attrs"
                    v-on="on"
                    @click="generarRequisicionDesdePrograma(item)"
                  >
                    <v-icon small>mdi-file-document-outline</v-icon>
                  </v-btn>
                </template>
                <span>Generar requisición</span>
              </v-tooltip>
              
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    x-small
                    icon
                    color="error"
                    class="mx-1"
                    v-bind="attrs"
                    v-on="on"
                    @click="confirmarEliminarPagoProgramado(item)"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>Eliminar pago programado</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card-text>

      <!-- Modal para confirmar eliminación de pago programado -->
      <v-dialog v-model="dialogConfirmacionPago" max-width="400px">
        <v-card>
          <v-card-title class="headline">
            Confirmar Eliminación
          </v-card-title>
          <v-card-text>
            ¿Está seguro que desea eliminar este pago programado?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="grey darken-1"
              text
              @click="dialogConfirmacionPago = false"
            >
              Cancelar
            </v-btn>
            <v-btn
              color="error"
              @click="eliminarPagoProgramado"
              :loading="eliminandoPago"
            >
              Eliminar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Modal para agregar/editar pago programado -->
      <v-dialog v-model="dialogPagoProgramado" max-width="600px">
        <v-card>
          <v-card-title class="headline primary white--text">
            {{ modoEdicion ? 'Editar' : 'Nuevo' }} Pago Programado
            <v-spacer></v-spacer>
            <v-btn icon dark @click="dialogPagoProgramado = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="pt-4">
            <v-form ref="formPagoProgramado" v-model="formValido">
              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="nuevoPago.monto_solicitado"
                    label="Monto*"
                    type="number"
                    prefix="$"
                    :rules="[v => !!v || 'El monto es requerido', v => v > 0 || 'El monto debe ser mayor a 0']"
                    required
                  ></v-text-field>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-autocomplete
                    v-model="nuevoPago.proveedor"
                    :items="proveedores"
                    item-text="nombre"
                    item-value="id"
                    label="Proveedor*"
                    :rules="[v => !!v || 'El proveedor es requerido']"
                    required
                  ></v-autocomplete>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-autocomplete
                    v-model="nuevoPago.ciclo"
                    :items="ciclos"
                    item-text="nombre"
                    item-value="id"
                    label="Ciclo*"
                    :rules="[v => !!v || 'El ciclo es requerido']"
                    required
                  ></v-autocomplete>
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="nuevoPago.servicio"
                    label="Servicio/Elemento de compra*"
                    :rules="[v => !!v || 'El servicio es requerido']"
                    required
                  ></v-text-field>
                </v-col>

                <v-col cols="12" v-if="modoEdicion && evidenciaUrl">
                  <div class="d-flex align-center mb-2">
                    <span class="mr-2">Evidencia actual:</span>
                    <v-btn
                      x-small
                      icon
                      color="primary"
                      @click="verEvidencia(evidenciaActual)"
                    >
                      <v-icon small>mdi-image</v-icon>
                    </v-btn>
                  </div>
                </v-col>

                <v-col cols="12">
                  <v-file-input
                    v-model="nuevoPago.evidencia"
                    label="Evidencia de pago (opcional)"
                    accept="image/png, image/jpeg, image/jpg, image/webp"
                    prepend-icon="mdi-file-image"
                    :show-size="true"
                    hint="Formatos aceptados: JPG, JPEG, PNG, WEBP"
                    persistent-hint
                    clearable
                  ></v-file-input>
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="nuevoPago.comentarios"
                    label="Comentarios"
                    rows="2"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>

          <v-col cols="12">
            <v-divider class="my-3"></v-divider>
            <v-switch
              v-model="generarPagosRecurrentes"
              label="Generar pagos recurrentes según la recurrencia del servicio"
              hint="Activar para generar automáticamente los pagos futuros"
              persistent-hint
            ></v-switch>
          </v-col>

          <v-col cols="12" v-if="generarPagosRecurrentes">
            <v-menu
              v-model="menuFechaLimite"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="fechaLimiteRecurrencia"
                  label="Generar pagos hasta esta fecha"
                  prepend-icon="mdi-calendar-end"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  :hint="`Se generarán aproximadamente ${cantidadPagosAGenerar} pagos`"
                  persistent-hint
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="fechaLimiteRecurrencia"
                @input="menuFechaLimite = false; calcularCantidadPagos()"
                locale="es-mx"
                :min="nuevoPago.fechaVencimiento"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="grey darken-1"
              text
              @click="dialogPagoProgramado = false"
            >
              Cancelar
            </v-btn>
            <v-btn
              color="success"
              @click="guardarPagoProgramado"
              :loading="guardandoPago"
              :disabled="!formValido"
            >
              {{ modoEdicion ? 'Actualizar' : 'Guardar' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="dialogEvidencia" max-width="800px">
        <v-card>
          <v-card-title class="headline primary white--text">
            Evidencia de pago
            <v-spacer></v-spacer>
            <v-btn icon dark @click="dialogEvidencia = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-0 text-center">
            <v-img
              :src="evidenciaUrl"
              max-height="600"
              contain
              class="ma-3"
            ></v-img>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <!-- <v-btn
              color="primary"
              text
              :href="evidenciaUrl"
              target="_blank"
            >
              Ver en tamaño completo
            </v-btn> -->
            <v-btn
              color="grey darken-1"
              text
              @click="dialogEvidencia = false"
            >
              Cerrar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="dialogConfirmacionMultiples" max-width="600px">
        <v-card>
          <v-card-title class="headline primary white--text">
            Confirmar creación de pagos recurrentes
            <v-spacer></v-spacer>
            <v-btn icon dark @click="dialogConfirmacionMultiples = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="pt-4">
            <p>Se crearán los siguientes pagos programados:</p>

            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Monto</th>
                    <th>Ciclo (estimado)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(pago, index) in pagosACrear" :key="index">
                    <td>{{ formatFecha(pago.fecha) }}</td>
                    <td>{{ formatCurrency(nuevoPago.monto_solicitado) }}</td>
                    <td>{{ pago.nombreCiclo || 'Por determinar' }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <v-alert
              v-if="pagosACrear.some(p => !p.idCiclo)"
              type="info"
              dense
              class="mt-3"
            >
              Algunos ciclos no existen en el sistema y se asignarán automáticamente.
            </v-alert>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="grey darken-1"
              text
              @click="dialogConfirmacionMultiples = false"
            >
              Cancelar
            </v-btn>
            <v-btn
              color="success"
              @click="confirmarCreacionMultiplesPagos"
              :loading="guardandoPago"
            >
              Crear pagos
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </template>

  <script>
  export default {
    name: 'PagosProgramados',

    props: {
      servicio: {
        type: Object,
        required: true
      }
    },

    data: () => ({
      dialogPagoProgramado: false,
      dialogConfirmacionPago: false,
      loadingPagosProgramados: false,
      guardandoPago: false,
      eliminandoPago: false,
      modoEdicion: false,
      pagoSeleccionado: null,
      pagoAEliminar: null,
      formValido: false,
      menuFecha: false,
      loadingProveedores: false,
      dialogEvidencia: false,
      evidenciaUrl: null,
      evidenciaActual: null,
      generarPagosRecurrentes: false,
      fechaLimiteRecurrencia: null,
      menuFechaLimite: false,
      cantidadPagosAGenerar: 0,
      dialogConfirmacionMultiples: false,
      pagosACrear: [],

      nuevoPago: {
        id_pago: null,
        monto_solicitado: 0,
        fechaVencimiento: new Date().toISOString().substr(0, 10),
        proveedor: null,
        servicio: '',
        ciclo: null,
        comentarios: '',
        imagen: null
      },

      pagosProgramados: [],

      pagosProgramadosHeaders: [
        { text: 'Fecha Vencimiento', value: 'fechaVencimiento' },
        { text: 'Monto', value: 'monto_solicitado' },
        { text: 'Proveedor', value: 'proveedorNombre' },
        { text: 'Servicio', value: 'nombre_servicio' },
        { text: 'Ciclo', value: 'cicloNombre' },
        { text: 'Comentarios', value: 'comentarios' },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
      ],

      // Datos para los dropdowns
      proveedores: [],

      ciclos: [],
      loadingCiclos: false
    }),

    created( ){
      this.nuevoPago.fechaVencimiento = this.servicio.fecha_vencimiento
      this.nuevoPago.monto_solicitado = this.servicio.monto_solicitado

    },

    methods: {
      generarRequisicionDesdePrograma(pago) {
        // console.log('PagosProgramados: Emitiendo evento generar-requisicion con pago:', pago);
        // Emitir un evento al componente padre con el pago seleccionado
        this.$emit('generar-requisicion', pago);
      },

      verEvidencia(nombreArchivo) {
        if (!nombreArchivo) return;

        // Establecer la URL y registrarla en consola para depuración
        this.evidenciaUrl = `/imagenes-servicios/${nombreArchivo}`;
        console.log("URL de evidencia:", this.evidenciaUrl);

        // Abrir el diálogo solo si se llama explícitamente (no desde editarPagoProgramado)
        if (!this.dialogPagoProgramado) {
          this.dialogEvidencia = true;
        }
      },

      formatFecha(fecha) {

        const fechaAjustada = new Date(`${fecha}T12:00:00`);
        return fechaAjustada.toLocaleDateString('es-MX', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      },

      formatCurrency(value) {
        return new Intl.NumberFormat('es-MX', {
          style: 'currency',
          currency: 'MXN'
        }).format(value);
      },

      async cargarProveedores() {
        this.loadingProveedores = true;
        try {
          const response = await this.$http.get("proveedores.list.erp");
          // Mapear la respuesta para adaptarla al formato que necesita el componente
          this.proveedores = response.data.map(proveedor => ({
            id: proveedor.idproveedores, // Usamos el mismo nombre de campo que en la base de datos
            nombre: proveedor.nombre_comercial // Usamos el campo que contiene el nombre del proveedor
          }));
        } catch (error) {
          console.error('Error al cargar proveedores:', error);
          this.$emit('mostrar-error', 'Error al cargar la lista de proveedores');
        } finally {
          this.loadingProveedores = false;
        }
      },

      async cargarPagosProgramados() {
        this.loadingPagosProgramados = true;
        try {
          // Obtener el ID correcto del servicio
          const idServicio = this.servicio.id_servicio_sistema || this.servicio.idservicios_sistemas;

          // Llamada real a la API
          const response = await this.$http.get(`servicios_sistemas.list.pagos/${idServicio}`);

          // Mapear los datos recibidos para adaptarlos al formato que necesita el componente
          this.pagosProgramados = response.data.map(pago => {
            // Buscar nombres de proveedor y ciclo para mostrar
            let proveedor = this.proveedores.find(p => p.id === pago.id_proveedor);
            let ciclo = this.ciclos.find(c => c.id === pago.id_ciclo);

            // Si no se encuentran en las listas predefinidas, usar los nombres incluidos en la respuesta (si existen)
            const proveedorNombre = proveedor ? proveedor.nombre : (pago.id_proveedor_nombre || 'No especificado');
            const cicloNombre = ciclo ? ciclo.nombre : (pago.id_ciclo_nombre || 'No especificado');
            return {
              id_pago: pago.id_pago,
              monto_solicitado: pago.monto_solicitado,
              fechaVencimiento: pago.fecha_vencimiento,
              fecha_pago: pago.fecha_pago,
              monto_pagado: pago.monto_pagado,
              diferencia_pago: pago.diferencia_pago,
              id_requisicion: pago.id_requisicion,
              id_proveedor: pago.id_proveedor,
              proveedor: pago.id_proveedor,
              proveedorNombre: proveedorNombre,
              nombre_servicio: pago.nombre_servicio || 'No especificado',
              servicio: pago.nombre_servicio,
              id_ciclo: pago.id_ciclo,
              ciclo: pago.id_ciclo,
              cicloNombre: cicloNombre,
              comentarios: pago.comentarios || '',
              evidencia: pago.evidencia
            };
          });
        } catch (error) {
          console.error('Error al cargar pagos programados:', error);
          this.$emit('mostrar-error', 'Error al cargar los pagos programados');
        } finally {
          this.loadingPagosProgramados = false;
        }
      },


      abrirModalPagoProgramado() {
        this.modoEdicion = false;

        const proveedorId = this.servicio.idproveedores;

        const nombreServicio = this.servicio.egreso_programado;

        this.nuevoPago = {
          id_pago: null,
          monto_solicitado: this.servicio.monto_solicitado,
          fechaVencimiento: this.servicio.fecha_vencimiento,
          proveedor: proveedorId,
          servicio: nombreServicio,
          ciclo: null,
          comentarios: '',
          imagen: null
        };

        this.dialogPagoProgramado = true;
      },

      editarPagoProgramado(item) {
        this.modoEdicion = true;
        this.nuevoPago = {
          id_pago: item.id_pago,
          monto_solicitado: item.monto_solicitado,
          fechaVencimiento: item.fechaVencimiento,
          proveedor: item.id_proveedor,
          servicio: item.nombre_servicio,
          ciclo: item.id_ciclo,
          comentarios: item.comentarios || '',
          evidencia: null
        };
        // Si hay evidencia, guardamos la URL para mostrarla
        if (item.evidencia) {
          this.evidenciaActual = item.evidencia;
          this.evidenciaUrl = `./../../imagenes-servicios/${item.evidencia}`;
        } else {
          this.evidenciaActual = null;
          this.evidenciaUrl = null;
        }
        this.dialogPagoProgramado = true;
      },

      async buscarCicloPorFecha(fecha) {
        if (!fecha) return null;

        try {
          const response = await this.$http.get(`ciclos.find.by.fecha/${fecha}/${this.servicio.plantel}`);

          if (response.data && response.data.id_ciclo) {
            // Actualizar el ciclo seleccionado en el formulario
            this.nuevoPago.ciclo = response.data.id_ciclo;
            // console.log(`Ciclo encontrado para la fecha ${fecha}: ${response.data.ciclo}`);
            return response.data.id_ciclo;
          }
          return null;
        } catch (error) {
          console.warn(`No se encontró un ciclo para la fecha ${fecha}`, error);
          // No mostramos error al usuario, simplemente devolvemos null
          return null;
        }
      },


      async cargarCiclos() {
        this.loadingCiclos = true;
        try {
          const response = await this.$http.get("ciclos.list");
          // Mapear la respuesta para adaptarla al formato que necesita el componente
          this.ciclos = response.data.map(ciclo => ({
            id: ciclo.id_ciclo,
            nombre: ciclo.ciclo,
            fechaInicio: ciclo.fecha_inicio_ciclo,
            fechaFin: ciclo.fecha_fin_ciclo,
            activo: ciclo.activo_sn === '1' || ciclo.activo_sn === 1
          }));

          // Filtrar solo ciclos activos y ordenar por fecha de inicio (más recientes primero)
          this.ciclos = this.ciclos
            .filter(ciclo => ciclo.activo)
            .sort((a, b) => new Date(b.fechaInicio) - new Date(a.fechaInicio));

        } catch (error) {
          console.error('Error al cargar ciclos:', error);
          this.$emit('mostrar-error', 'Error al cargar la lista de ciclos');
        } finally {
          this.loadingCiclos = false;
        }
      },


      async guardarPagoProgramado() {
        if (!this.$refs.formPagoProgramado.validate()) return;

        // Si no estamos generando pagos recurrentes o estamos en modo edición, usar la lógica original
        if (!this.generarPagosRecurrentes || this.modoEdicion) {
          // Llamar al método original
          await this.guardarPagoSimple();
        } else {
          // LÓGICA PARA PAGOS RECURRENTES
          // Generar fechas según la recurrencia
          const fechasGeneradas = this.generarFechasRecurrentes(
            this.nuevoPago.fechaVencimiento,
            this.fechaLimiteRecurrencia,
            this.servicio.recurrencia
          );

          // Preparar el resumen de pagos a crear
          this.pagosACrear = [];

          for (let i = 0; i < fechasGeneradas.length; i++) {
            const fechaPago = fechasGeneradas[i].fecha;

            // Buscar ciclo para esta fecha
            let cicloPago = await this.buscarCicloPorFecha(fechaPago);
            let nombreCiclo, idCiclo;

            // Si no encontramos un ciclo en la base de datos, inferimos el nombre
            if (!cicloPago) {

              // Lo brincamoss
              continue;

              const nombreCicloInferido = this.inferirNombreCiclo(fechaPago);
              const cicloInfo = this.buscarCicloPorNombre(nombreCicloInferido);

              // Si tenemos un ID de ciclo, lo usamos
              if (cicloInfo) {
                idCiclo = cicloInfo.id;
                nombreCiclo = cicloInfo.nombre;
              } else {

                continue
                // Si no tenemos ID, usamos el ciclo del primer pago como fallback para el primer pago
                // idCiclo = i === 0 ? this.nuevoPago.ciclo : null;
                // nombreCiclo = cicloInfo.nombre;
              }
            } else {
              // Si encontramos un ciclo, obtenemos su nombre
              idCiclo = cicloPago;
              const cicloSeleccionado = this.ciclos.find(c => c.id === cicloPago);
              nombreCiclo = cicloSeleccionado ? cicloSeleccionado.nombre : `Ciclo para ${fechaPago}`;
            }

            // Añadir a la lista de pagos a crear
            this.pagosACrear.push({
              fecha: fechaPago,
              idCiclo: idCiclo,
              nombreCiclo: nombreCiclo
            });
          }

          // Mostrar diálogo de confirmación
          this.dialogConfirmacionMultiples = true;
        }
      },

      // Nuevo método para la confirmación de creación de múltiples pagos
      async confirmarCreacionMultiplesPagos() {
        this.dialogConfirmacionMultiples = false;
        this.guardandoPago = true;

        try {
          // Obtener el ID correcto del servicio
          const idServicio = this.servicio.id_servicio_sistema || this.servicio.idservicios_sistemas;

          // Buscar el nombre del proveedor seleccionado
          const proveedorSeleccionado = this.proveedores.find(p => p.id === this.nuevoPago.proveedor);
          const nombreProveedor = proveedorSeleccionado ? proveedorSeleccionado.nombre : 'No especificado';

          // Guardar múltiples pagos
          for (let i = 0; i < this.pagosACrear.length; i++) {
            const pago = this.pagosACrear[i];

            // Crear FormData para este pago
            const formData = new FormData();

            // Añadir todos los campos del formulario al FormData
            formData.append('id_servicio', idServicio);
            formData.append('monto_solicitado', this.nuevoPago.monto_solicitado);
            formData.append('fecha_vencimiento', pago.fecha);
            formData.append('id_proveedor', this.nuevoPago.proveedor);
            formData.append('id_proveedor_nombre', nombreProveedor);
            formData.append('nombre_servicio', this.nuevoPago.servicio);
            formData.append('id_ciclo', pago.idCiclo || this.nuevoPago.ciclo); // Usar el ciclo inferido o el predeterminado
            formData.append('id_ciclo_nombre', pago.nombreCiclo);
            formData.append('comentarios', this.nuevoPago.comentarios);
            formData.append('monto_pagado', 0);

            // Solo añadir evidencia al primer pago
            if (i === 0 && this.nuevoPago.evidencia) {
              formData.append('evidencia', this.nuevoPago.evidencia);
            }

            // Configuración para la petición con FormData
            const config = {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            };

            // Enviar la solicitud para crear este pago
            await this.$http.post(
              'servicios_sistemas.add.pago.erp',
              formData,
              config
            );
          }

          // Mostrar mensaje de éxito con la cantidad de pagos creados
          this.$emit('mostrar-exito', `Se han creado ${this.pagosACrear.length} pagos programados correctamente`);

          // Recargar la lista completa
          await this.cargarPagosProgramados();

          // Cerrar el modal
          this.dialogPagoProgramado = false;
        } catch (error) {
          console.error('Error al guardar pagos programados:', error);
          const errorMessage = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Error al guardar los pagos programados';

          this.$emit('mostrar-error', errorMessage);
        } finally {
          this.guardandoPago = false;
        }
      },

      // Extraer la lógica original de guardar un solo pago en un método separado
      async guardarPagoSimple() {
        this.guardandoPago = true;

        try {
          // Obtener el ID correcto del servicio
          const idServicio = this.servicio.id_servicio_sistema || this.servicio.idservicios_sistemas;

          // Buscar el nombre del proveedor seleccionado
          const proveedorSeleccionado = this.proveedores.find(p => p.id === this.nuevoPago.proveedor);
          const nombreProveedor = proveedorSeleccionado ? proveedorSeleccionado.nombre : 'No especificado';

          // Buscar el nombre del ciclo seleccionado
          const cicloSeleccionado = this.ciclos.find(c => c.id === this.nuevoPago.ciclo);
          const nombreCiclo = cicloSeleccionado ? cicloSeleccionado.nombre : 'No especificado';

          // Crear FormData para enviar archivos
          const formData = new FormData();

          // Añadir todos los campos del formulario al FormData
          formData.append('id_servicio', idServicio);
          formData.append('monto_solicitado', this.nuevoPago.monto_solicitado);
          formData.append('fecha_vencimiento', this.nuevoPago.fechaVencimiento);
          formData.append('id_proveedor', this.nuevoPago.proveedor);
          formData.append('id_proveedor_nombre', nombreProveedor);
          formData.append('nombre_servicio', this.nuevoPago.servicio);
          formData.append('id_ciclo', this.nuevoPago.ciclo);
          formData.append('id_ciclo_nombre', nombreCiclo);
          formData.append('comentarios', this.nuevoPago.comentarios);
          formData.append('monto_pagado', 0);

          if (this.modoEdicion && this.evidenciaActual && !this.nuevoPago.evidencia) {
            formData.append('evidencia_existente', this.evidenciaActual);
          }
          // Añadir la imagen si existe
          if (this.nuevoPago.evidencia) {
            formData.append('evidencia', this.nuevoPago.evidencia);
          }

          let response;

          // Configuración para la petición con FormData
          const config = {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          };

          if (this.modoEdicion) {
            // Actualizar pago existente
            response = await this.$http.put(
              `servicios_sistemas.update.pago.erp/${this.nuevoPago.id_pago}`,
              formData,
              config
            );
            this.$emit('mostrar-exito', 'Pago programado actualizado correctamente');
          } else {
            // Crear nuevo pago
            response = await this.$http.post(
              'servicios_sistemas.add.pago.erp',
              formData,
              config
            );
            this.$emit('mostrar-exito', 'Pago programado creado correctamente');
          }

          // Recargar la lista completa
          await this.cargarPagosProgramados();

          // Cerrar el modal
          this.dialogPagoProgramado = false;
        } catch (error) {
          console.error('Error al guardar pago programado:', error);
          const errorMessage = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Error al guardar el pago programado';

          this.$emit('mostrar-error', errorMessage);
        } finally {
          this.guardandoPago = false;
        }
      },


      confirmarEliminarPagoProgramado(item) {
        this.pagoAEliminar = item;
        this.dialogConfirmacionPago = true;
      },

      async eliminarPagoProgramado() {
        this.eliminandoPago = true;

        try {
          // Llamada real a la API para eliminar
          await this.$http.delete(`servicios_sistemas.delete.pago.erp/${this.pagoAEliminar.id_pago}`);

          // Mostrar mensaje de éxito
          this.$emit('mostrar-exito', 'Pago programado eliminado correctamente');

          // Actualizar lista de pagos programados
          await this.cargarPagosProgramados();

          // Cerrar el diálogo de confirmación
          this.dialogConfirmacionPago = false;
          this.pagoAEliminar = null;
        } catch (error) {
          console.error('Error al eliminar pago programado:', error);
          const errorMessage = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Error al eliminar el pago programado';

          this.$emit('mostrar-error', errorMessage);
        } finally {
          this.eliminandoPago = false;
        }
      },
      calcularCantidadPagos() {
        if (!this.fechaLimiteRecurrencia || !this.nuevoPago.fechaVencimiento) {
          this.cantidadPagosAGenerar = 0;
          return;
        }

        const fechaInicial = new Date(this.nuevoPago.fechaVencimiento);
        const fechaLimite = new Date(this.fechaLimiteRecurrencia);

        // Obtener la recurrencia del servicio
        const recurrencia = this.servicio.recurrencia ? parseInt(this.servicio.recurrencia) : 0;

        console.log( 'recurrencia',  recurrencia )

        if (recurrencia === 0) {
          this.cantidadPagosAGenerar = 1; // No aplica recurrencia
          return;
        }

        let cantidadPagos = 0;

        switch (recurrencia) {
          case 1: // Diario
            cantidadPagos = Math.floor((fechaLimite - fechaInicial) / (1000 * 60 * 60 * 24)) + 1;
            break;
          case 2: // Mensual
            cantidadPagos = (fechaLimite.getFullYear() - fechaInicial.getFullYear()) * 12 +
                            fechaLimite.getMonth() - fechaInicial.getMonth() + 1;
            break;
          case 3: // Bimestral
            cantidadPagos = Math.floor(((fechaLimite.getFullYear() - fechaInicial.getFullYear()) * 12 +
                            fechaLimite.getMonth() - fechaInicial.getMonth() + 1) / 2) + 1;
            break;
          case 4: // Anual
            cantidadPagos = fechaLimite.getFullYear() - fechaInicial.getFullYear() + 1;
            break;
          case 5: // Catorcenal
            cantidadPagos = Math.floor((fechaLimite - fechaInicial) / (1000 * 60 * 60 * 24 * 14)) + 1;
            break;
          case 6: // Semanal
            cantidadPagos = Math.floor((fechaLimite - fechaInicial) / (1000 * 60 * 60 * 24 * 7)) + 1;
            break;
        }

        this.cantidadPagosAGenerar = Math.max(1, cantidadPagos);
      },

      /**
        * Genera un array de fechas recurrentes según el tipo de recurrencia
       * @param {Date} fechaInicial - Fecha del primer pago
       * @param {Date} fechaLimite - Fecha límite hasta donde generar pagos
       * @param {Number} tipoRecurrencia - Tipo de recurrencia (1: Diario, 2: Mensual, etc.)
       * @return {Array} - Array de objetos con fechas calculadas
       */
      generarFechasRecurrentes(fechaInicial, fechaLimite, tipoRecurrencia) {
        // Convertir a objetos Date si son strings
        fechaInicial = new Date(fechaInicial);
        fechaLimite = new Date(fechaLimite);

        console.log( 'tipoRecurrencia', tipoRecurrencia )

        // Si no hay recurrencia o la recurrencia es "No Aplica" (0), solo devolver la fecha inicial
        if (!tipoRecurrencia || tipoRecurrencia === '0' || tipoRecurrencia === 0) {
          return [{
            fecha: fechaInicial.toISOString().substr(0, 10)
          }];
        }

        const fechas = [];
        // Añadir el primer pago (fecha inicial)
        fechas.push({
          fecha: fechaInicial.toISOString().substr(0, 10)
        });

        // Convertir a número si es string
        tipoRecurrencia = parseInt(tipoRecurrencia);

        // formatFecha 
        console.log( 'fechaInicial', fechaInicial.toISOString().substr(0, 10) )

        // Clonar la fecha inicial para no modificarla
        let fechaActual = new Date(`${fechaInicial.toISOString().substr(0, 10)}T12:00:00`);
        console.log( 'Actual',fechaActual )
        // Generar las fechas según el tipo de recurrencia
        while (true) {
          let nuevaFecha = new Date(fechaActual);

          switch (tipoRecurrencia) {
            case 1: // Diario
              nuevaFecha.setDate(nuevaFecha.getDate() + 1);
              break;
            case 2: // Mensual
              nuevaFecha.setMonth(nuevaFecha.getMonth() + 1);
              break;
            case 3: // Bimestral
              nuevaFecha.setMonth(nuevaFecha.getMonth() + 2);
              break;
            case 4: // Anual
              nuevaFecha.setFullYear(nuevaFecha.getFullYear() + 1);
              break;
            case 5: // Catorcenal
              nuevaFecha.setDate(nuevaFecha.getDate() + 14);
              break;
            case 6: // Semanal
              nuevaFecha.setDate(nuevaFecha.getDate() + 7);
              break;
            default:
              return fechas; // Si hay un tipo de recurrencia no reconocido, devolver lo que tenemos
          }

          // Si la nueva fecha excede la fecha límite, terminamos
          if (nuevaFecha > fechaLimite) {
            break;
          }

          // Manejar el caso del último día del mes (ej. 31 de enero → 28/29 de febrero)
          const esFebrero = nuevaFecha.getMonth() === 1; // Febrero es el mes 1 (0-indexed)
          const esUltimoDiaMes = fechaActual.getDate() >= 28;

          if (esUltimoDiaMes && esFebrero) {
            // Si estamos en febrero, usar el último día disponible de febrero
            const ultimoDiaFebrero = new Date(nuevaFecha.getFullYear(), 2, 0).getDate();
            nuevaFecha.setDate(Math.min(fechaActual.getDate(), ultimoDiaFebrero));
          }
          else if (fechaActual.getDate() > 28) {
            // Para otros meses con último día variable (30/31)
            const ultimoDiaMes = new Date(nuevaFecha.getFullYear(), nuevaFecha.getMonth() + 1, 0).getDate();
            if (fechaActual.getDate() > ultimoDiaMes) {
              nuevaFecha.setDate(ultimoDiaMes);
            }
          }

          // Añadir la nueva fecha al array
          fechas.push({
            fecha: nuevaFecha.toISOString().substr(0, 10)
          });

          // Actualizar la fecha actual para la próxima iteración
          fechaActual = nuevaFecha;
        }

        return fechas;
      },
      /**
     * Genera un nombre de ciclo basado en una fecha
     * @param {Date|string} fecha - La fecha para generar el nombre del ciclo
     * @return {string} - Nombre inferido del ciclo (formato MM_AA)
     */
    inferirNombreCiclo(fecha) {
      const fechaObj = new Date(fecha);
      const mes = String(fechaObj.getMonth() + 1).padStart(2, '0');
      const anio = String(fechaObj.getFullYear()).slice(-2);
      return `${mes}_${anio}`;
    },

    /**
     * Busca un ciclo por su nombre inferido o crea un objeto temporal con la información
     * @param {string} nombreCiclo - Nombre del ciclo (formato MM_AA)
     * @return {Object} - Objeto con información del ciclo (id, nombre)
     */
    buscarCicloPorNombre(nombreCiclo) {
      // Primero buscamos en los ciclos existentes
      const cicloExistente = this.ciclos.find(c => c.nombre.includes(nombreCiclo));

      if (cicloExistente) {
        return {
          id: cicloExistente.id,
          nombre: cicloExistente.nombre
        };
      }

      // Si no existe, creamos un objeto temporal para mostrar al usuario

      return null 
      return {
        id: null, // No tenemos ID todavía
        nombre: `Ciclo ${nombreCiclo} (pendiente de creación)`
      };
    },
    },
    watch: {
      // ... watchers existentes

      'nuevoPago.fechaVencimiento': function() {
        if (this.generarPagosRecurrentes) {
          this.calcularCantidadPagos();
        }
      },

      'generarPagosRecurrentes': function(val) {
        if (val && !this.fechaLimiteRecurrencia) {
          // Por defecto, establecer la fecha límite a un año desde la fecha inicial
          const fechaInicial = new Date(this.nuevoPago.fechaVencimiento);
          const fechaUnAnioDelante = new Date(fechaInicial);
          fechaUnAnioDelante.setFullYear(fechaInicial.getFullYear() + 1);
          this.fechaLimiteRecurrencia = fechaUnAnioDelante.toISOString().substr(0, 10);
          this.calcularCantidadPagos();
        }
      }
    },

    mounted() {
      this.cargarPagosProgramados();
      this.cargarProveedores();
      this.cargarCiclos();
    }
  }
  </script>
